/**
 * sdkBiddingSection.js - FALLBACK twin of proxy-server/sdkBidding/section.js (the "SDK bidding"
 * sign-off section: buyer by unit, first break, report-only demand health). Pinned field by
 * field against the proxy module by sdkBiddingParity.test.js.
 *
 * The proxy's rollup `sdkBidding` payload is the answer. This copy runs only when a caller
 * explicitly opts in because the payload is absent (older proxy, or no rollup fetched); the
 * result is labelled source 'browser-fallback' so the report says so. It has no env flag: the
 * caller passes `mode` ('shadow' | 'on'; 'off' returns null).
 *
 * Contract: docs/sdlc/sdk-bidding-signoff/20-requirements.md ACs 28-44, 50-58, 59, 73-86.
 * Findings are warn or lower and never feed criteria, signable or blockers. No price field.
 * Never throws.
 */

import { rowTokenStates } from './sdkBiddingA3p';
import {
  BUYER_ADAPTER_CLASSES, msidFromUrl, resolveSessionMapping, selectMapping, unitEntries,
} from './sdkBiddingMapping';
import { metaAdRequestFor, isTestAuction } from './metaTestMode';
import { parseMetaAdRequest } from './metaWire';
import { FILL_RESULT_LABEL, fillResultForGam, adapterErrorName } from './mediationFillResult';
import {
  NO_REPORT_NOTE, NO_MAPPING_NOTE, USB_NOTE, STALE_NOTE,
  packForIu, placementChain, evaluateConfigPlacements, captureTimeMs,
} from './sdkBiddingPlacements';
import { getPacks } from '../api/appPacks';

export const BUYERS = ['meta', 'liftoff'];
const LABEL = { meta: 'Meta', liftoff: 'Liftoff' };

export const CODES = {
  token: 'sdk_bidding_token_missing',
  adFetch: 'sdk_bidding_ad_not_fetched',
  testModeGam: 'meta_test_mode_mismatch',
  testModeSdk: 'sdk_bidding_test_mode_sdk_only',
  fill: 'sdk_bidding_fill_failed',
  bundle: 'sdk_bidding_bundle_mismatch',
};

// Copy of proxy sdkBidding/section.js COPY. The parity test compares the two.
export const COPY = {
  usb: 'Not available on USB capture',
  usbLong: 'Not available on USB capture (request only).',
  unreadable: 'Unreadable: the response body was not retained for this row. Not treated as no win.',
  noMapping: 'Unit mapping not captured this session. Cold-launch the app with the proxy on to capture it. Token facts below are observed, not graded.',
  mappingUsb: 'Unit mapping not available on USB capture.',
  mappingUnreadable: 'Unit mapping unreadable: the pubsetting response body was not retained. Token facts below are observed, not graded.',
  mappingMalformed: 'Unit mapping not captured this session (the pubsetting body did not parse). Token facts below are observed, not graded.',
  mappingAmbiguous: 'Unit mapping ambiguous: several app_name mappings and no GAM msid matches one. Not graded.',
  metaSideNone: 'Not verifiable from this capture (no Meta ad fetch observed).',
  metaSideManual: "Manual: confirm the Meta app id and bundle in Meta's dashboard. Not verifiable from a capture.",
  metaAppId: "Manual: confirm the Meta app id in Meta's dashboard. Not verifiable from a capture.",
  render: 'Manual: confirm the creative on the device. A capture cannot prove a Meta render.',
  liftoffNa: 'n/a: not graded for Liftoff in this version.',
  testModeLiftoffNa: 'n/a: Meta test mode only.',
  noWin: (b) => `No ${LABEL[b]} win in this session (demand, not a break).`,
  notInSession: 'Not in this session.',
  noPrevious: 'No previous build to compare (build-over-build is qaRunner runs only).',
  demandHeader: 'Demand health. Report only; does not gate sign-off.',
  tokenUnknownTruncated: 'Unknown: the captured URL was cut at about 4KB and the token field did not survive. Not treated as missing.',
  placementsNoReport: NO_REPORT_NOTE,
  placementsNoMapping: NO_MAPPING_NOTE,
  placementsUsb: USB_NOTE,
  placementsStale: STALE_NOTE,
  placementsPartial: 'SDK request not captured',
  placementsAndroid: 'Android: GAM config and response checked; Meta SDK request not captured.',
  placementsNotGraded: 'Format type recorded, not graded (only banner verified).',
  placementsSuppressed: 'suppressed (report stale)',
};

const ADAPTER_TO_BUYER = {};
Object.keys(BUYER_ADAPTER_CLASSES).forEach((id) => {
  BUYER_ADAPTER_CLASSES[id].forEach((cls) => { ADAPTER_TO_BUYER[cls] = id; });
});

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
const isUsb = (r) => !!r && r.captureSource === 'logcat';
const rowKey = (r) => (r && (r._k != null ? r._k : (r.id != null ? r.id : null)));
const typeOf = (r) => String((r && (r.requestType || r.type)) || '').toLowerCase();
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function iuOf(url) {
  const m = /[?&]iu=([^&#]*)/.exec(String(url || ''));
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

function gamInfo(row) {
  const url = String(row.url || '');
  const vd = row.validData && typeof row.validData === 'object' ? row.validData : {};
  const ex = vd.expanded && typeof vd.expanded === 'object' ? vd.expanded : {};
  const usb = isUsb(row);
  const readable = !usb && has(ex, 'adapter');
  const adapter = readable ? ex.adapter : null;
  let winner = null;
  if (readable && typeof adapter === 'string' && adapter) {
    winner = has(ADAPTER_TO_BUYER, adapter) ? ADAPTER_TO_BUYER[adapter] : 'other';
  }
  return {
    row, usb, readable, winner, adapter,
    iu: iuOf(url),
    adtest: /[?&]adtest=on(?:&|$)/.test(url),
    msid: msidFromUrl(url),
    tokens: rowTokenStates(row),
    sdkBidId: ex.sdkBidId != null ? String(ex.sdkBidId) : null,
    key: rowKey(row),
    sel: null,
  };
}

const tokenOf = (g, buyer) => g.tokens.find((t) => t.buyer === buyer) || null;
const stateOf = (g, buyer) => { const t = tokenOf(g, buyer); return t ? t.state : 'absent'; };

export function metaFetchRows(list) {
  const out = [];
  for (const r of list) {
    if (!r || typeof r.url !== 'string') continue;
    let m = r.validData && typeof r.validData === 'object' ? r.validData.metaAdRequest : null;
    if (!m && /network_ads_common/.test(r.url) && /facebook\.com/.test(r.url)) {
      m = parseMetaAdRequest(r.url, r.requestBody, r.responseBody);
    }
    if (m && m.bidId) out.push({ _k: rowKey(r), validData: { metaAdRequest: m } });
  }
  return out;
}

function tally(reqs, buyer) {
  const t = { present: 0, empty: 0, absent: 0, unknown: 0, total: 0 };
  for (const g of reqs) {
    const s = stateOf(g, buyer);
    t.total += 1;
    if (s === 'present') t.present += 1;
    else if (s === 'empty') t.empty += 1;
    else if (s === 'absent') t.absent += 1;
    else t.unknown += 1;
  }
  return t;
}

const na = (copy) => ({ status: 'n/a', copy });
const usbCell = () => ({ status: 'usb', copy: COPY.usb });
const finding = (code, msg, extra) => ({ code, level: 'warn', msg, ...(extra || {}) });

function tokenFinding(buyer, unit, t) {
  const bad = t.empty + t.absent;
  const where = unit ? `unit ${unit}` : 'this unit';
  const what = t.empty && !t.absent ? 'an empty signal' : (t.absent && !t.empty ? `no ${LABEL[buyer]} entry` : 'an empty signal or no entry');
  return finding(CODES.token,
    `${LABEL[buyer]} token missing: ${bad} of ${t.total} GAM requests for ${where} sent ${what} in a3p, so ${LABEL[buyer]} cannot bid on them. Check that the ${buyer === 'meta' ? 'Meta adapter' : 'Liftoff SDK'} initialises before the first ad request.`);
}

function fillName(ping) {
  if (!ping.failed) return 'filled (0.0)';
  const name = ping.errorCode ? adapterErrorName(ping.errorCode, 'Meta') : null;
  if (ping.errorCode) return name ? `${name} (${ping.errorCode})` : `error ${ping.errorCode}`;
  return ping.timedOut ? 'timed out' : 'failed to load';
}

function evalGroup(buyer, unit, reqs, ctx, mode) {
  const L = LABEL[buyer];
  const evidence = [];
  const row = { buyer, unit, availability: mode, render: 'manual', renderCopy: COPY.render };
  const unitWhere = unit ? `unit ${unit}` : 'this session';

  if (mode === 'usb') {
    const t = tally(reqs, buyer);
    row.token = { status: 'observed', ...t, copy: t.unknown && !t.present && !t.empty ? COPY.tokenUnknownTruncated : 'Token check n/a on USB capture: unit mapping not available. Observed token result shown here.' };
  } else if (mode === 'no_mapping') {
    const t = tally(reqs, buyer);
    row.token = { status: 'observed', ...t, copy: ctx.mappingCopy };
  } else {
    const mappedReqs = reqs.filter((g) => g.mappedBuyers && g.mappedBuyers.includes(buyer));
    row.mapped = mappedReqs.length > 0;
    if (!mappedReqs.length) {
      row.token = { status: 'n/a', ...tally([], buyer), copy: `Unit not mapped to ${L} bidding in the captured settings.` };
    } else {
      const t = tally(mappedReqs, buyer);
      const status = (t.empty + t.absent) > 0 ? 'warn' : (t.present > 0 ? 'ok' : 'unknown');
      row.token = { status, ...t };
      if (status === 'warn') evidence.push(tokenFinding(buyer, unit, t));
      if (status === 'unknown') row.token.copy = COPY.tokenUnknownTruncated;
    }
  }

  if (mode === 'usb') {
    Object.assign(row, {
      wins: usbCell(), adFetched: usbCell(), testMode: usbCell(), bundle: usbCell(), fill: usbCell(),
      firstBreak: null, demand: 'unavailable', evidence,
    });
    return row;
  }

  const readableReqs = reqs.filter((g) => g.readable);
  const winReqs = readableReqs.filter((g) => g.winner === buyer);
  row.wins = { count: winReqs.length, readable: readableReqs.length, unreadable: reqs.length - readableReqs.length, total: reqs.length };
  if (!readableReqs.length) { row.wins.copy = COPY.unreadable; row.demand = 'unreadable'; }
  else row.demand = winReqs.length ? 'win' : 'no win';
  if (row.demand === 'no win') row.wins.copy = COPY.noWin(buyer);

  if (mode === 'full') {
    const b = { status: 'n/a', agree: 0, mismatch: 0, notRun: 0, selection: null, appName: null };
    const sels = new Set();
    for (const g of reqs) {
      if (!g.sel || !g.sel.mapping) continue;
      sels.add(g.sel.selection);
      b.appName = g.sel.mapping.appName;
      b.platform = g.sel.mapping.platform;
      if (!g.msid) b.notRun += 1;
      else if (g.msid === g.sel.mapping.appName) b.agree += 1;
      else { b.mismatch += 1; b.msid = g.msid; }
    }
    b.selection = sels.size === 1 ? [...sels][0] : (sels.size ? 'mixed' : null);
    if (b.mismatch) {
      b.status = 'warn';
      evidence.push(finding(CODES.bundle,
        `Bundle mismatch: GAM msid "${b.msid}" differs from the captured publisher settings app_name "${b.appName}" on ${b.mismatch} of ${reqs.length} GAM requests for ${unitWhere}. The comparison is exact, so a .debug suffix counts. Confirm this is the build the settings were captured for.`));
    } else if (b.agree) b.status = 'ok';
    else if (b.notRun) {
      b.status = 'not_run';
      b.copy = b.platform === 'ios' ? 'not run: no msid on iOS requests' : 'not run: no msid on this request';
    }
    row.bundle = b;
  } else {
    row.bundle = na('n/a: unit mapping not captured.');
  }

  if (buyer !== 'meta') {
    row.adFetched = na(COPY.liftoffNa);
    row.testMode = na(COPY.testModeLiftoffNa);
    row.fill = na(COPY.liftoffNa);
  } else {
    if (!winReqs.length) {
      const why = row.demand === 'unreadable' ? COPY.unreadable : COPY.noWin('meta');
      row.adFetched = na(why); row.testMode = na(why); row.fill = na(why);
    } else {
      const joined = winReqs.map((g) => ({ g, fetch: metaAdRequestFor(g.row, ctx.fetchRows) }));
      const fetched = joined.filter((j) => j.fetch).length;
      const unjoinable = joined.filter((j) => !j.g.sdkBidId).length;
      const missing = joined.filter((j) => !j.fetch && j.g.sdkBidId).length;
      row.adFetched = { status: missing ? 'warn' : (fetched ? 'ok' : 'unknown'), wins: winReqs.length, fetched, missing, unjoinable };
      if (missing) {
        const hint = ctx.fetchRows.length === 0 ? ' No Meta ad fetch appears anywhere in the session; check that *.facebook.com is not SSL-excluded.' : '';
        evidence.push(finding(CODES.adFetch,
          `Meta won ${plural(winReqs.length, 'GAM auction', 'GAM auctions')} for ${unitWhere} but ${missing} of ${winReqs.length} had no Meta ad fetch carrying the win's BID_ID, so the SDK never asked Meta for the ad.${hint}`));
      }
      if (!fetched && !missing) row.adFetched.copy = 'Unknown: the winning responses carried no bid_id to join on.';

      const tm = { gamOnly: 0, sdkOnly: 0, consistent: 0 };
      for (const j of joined) {
        if (!j.fetch) continue;
        const gamTest = isTestAuction(j.g.row);
        const sdkTest = !!j.fetch.testMode;
        if (gamTest && !sdkTest) tm.gamOnly += 1;
        else if (!gamTest && sdkTest) tm.sdkOnly += 1;
        else tm.consistent += 1;
      }
      const matched = tm.gamOnly + tm.sdkOnly + tm.consistent;
      row.testMode = { status: (tm.gamOnly + tm.sdkOnly) ? 'warn' : (matched ? 'ok' : 'n/a'), ...tm, matched };
      if (!matched) row.testMode.copy = 'n/a: no matched Meta ad fetch.';
      if (tm.gamOnly) {
        evidence.push(finding(CODES.testModeGam,
          `Meta won ${tm.gamOnly} test auctions (adtest=on) for ${unitWhere}, but Meta's SDK asked for the ad without TEST_MODE=1 on ${tm.gamOnly} of ${matched} fetches. Meta needs test mode on both sides or it answers 1001 no fill.`));
      }
      if (tm.sdkOnly) {
        evidence.push(finding(CODES.testModeSdk,
          `Meta's SDK fetch carried TEST_MODE=1 on ${tm.sdkOnly} of ${matched} fetches for ${unitWhere}, but the GAM request was not a test auction (no adtest=on). Meta answers test ads while GAM sells live: the two sides disagree.`));
      }

      const pings = winReqs.map((g) => {
        const fr = fillResultForGam(g.row, ctx.fillRows);
        return fr ? { found: true, failed: fr.failed, timedOut: fr.timedOut, errorCode: fr.errorCode, status: fr.status, ttrMs: fr.ttrMs } : { found: false };
      });
      const found = pings.filter((p) => p.found);
      const failed = found.filter((p) => p.failed);
      row.fill = {
        status: failed.length ? 'warn' : (found.length ? 'ok' : 'unknown'),
        wins: winReqs.length, pinged: found.length, failed: failed.length,
        pings: found.map((p) => ({ status: p.status, label: fillName(p), failed: p.failed })),
      };
      if (!found.length) row.fill.copy = 'Unknown: no fill-result ping was captured for these wins.';
      if (failed.length) {
        const names = [...new Set(failed.map(fillName))].join(', ');
        evidence.push(finding(CODES.fill,
          `Meta won ${failed.length} of ${winReqs.length} GAM auctions for ${unitWhere} but its SDK reported ${names}: the slot stayed empty. The ping is the SDK's own fill result, not an impression.`));
      }
    }
    if (ctx.fetchRows.length === 0) row.bundle.metaSide = COPY.metaSideNone;
    else row.bundle.metaSide = COPY.metaSideManual;
    row.metaAppId = { status: 'manual', copy: COPY.metaAppId, appIds: ctx.metaAppIds(unit) };
  }

  let firstBreak = null;
  if (row.token.status === 'warn') firstBreak = 'token';
  else if (row.adFetched && row.adFetched.status === 'warn') firstBreak = 'adFetched';
  else if (row.fill && row.fill.status === 'warn') firstBreak = 'fill';
  row.firstBreak = firstBreak;
  row.evidence = evidence;
  return row;
}

export function deltaFor(previous, buyer, unit, cur) {
  try {
    if (!previous || typeof previous !== 'object') return null;
    const b = has(previous, buyer) ? previous[buyer] : null;
    const p = b && has(b, unit) ? b[unit] : null;
    const curWins = cur.liveWins == null ? 0 : cur.liveWins;
    if (!p) return { requests: cur.liveRequests, wins: curWins, newUnit: true };
    return { requests: cur.liveRequests - (Number(p.liveRequests) || 0), wins: curWins - (Number(p.liveWins) || 0) };
  } catch { return null; }
}

export function buildDemand(gam, presentFor, previous) {
  const out = [];
  const units = [...new Set(gam.map((g) => g.iu).filter(Boolean))].sort();
  for (const unit of units) {
    const rs = gam.filter((g) => g.iu === unit);
    for (const buyer of BUYERS) {
      if (!presentFor(buyer, unit, rs)) continue;
      const live = rs.filter((g) => !g.adtest);
      const liveWithToken = live.filter((g) => stateOf(g, buyer) === 'present');
      const liveMitm = live.filter((g) => !g.usb);
      const liveReadable = liveMitm.filter((g) => g.readable);
      const adtestExcluded = rs.filter((g) => g.adtest && stateOf(g, buyer) === 'present').length;
      const cur = { buyer, unit, liveRequests: liveWithToken.length };
      if (!liveMitm.length && live.length) {
        cur.liveWins = null; cur.winsCopy = `wins: ${COPY.usb}`; cur.availability = 'usb';
      } else if (liveMitm.length && !liveReadable.length) {
        cur.liveWins = null; cur.winsCopy = COPY.unreadable; cur.availability = 'full';
      } else {
        cur.liveWins = liveReadable.filter((g) => g.winner === buyer).length;
        // No live rows at all (every request was adtest) is still a full MITM capture, not USB.
        cur.availability = (liveMitm.length || !live.length) ? 'full' : 'usb';
      }
      cur.liveWinsUnreadable = liveMitm.length - liveReadable.length;
      cur.otherWins = liveReadable.filter((g) => g.winner === 'other').length;
      cur.adtestExcluded = adtestExcluded;
      cur.delta = deltaFor(previous, buyer, unit, cur);
      cur.gates = false;
      out.push(cur);
    }
  }
  return out;
}

function mappingCopyFor(reason) {
  switch (reason) {
    case 'usb': return COPY.mappingUsb;
    case 'unreadable': return COPY.mappingUnreadable;
    case 'malformed': return COPY.mappingMalformed;
    default: return COPY.noMapping;
  }
}

function observationsFor(mitm) {
  const obs = [];
  for (const buyer of BUYERS) {
    if (!mitm.some((g) => tokenOf(g, buyer) && tokenOf(g, buyer).entryFound)) continue;
    const t = tally(mitm, buyer);
    const parts = [];
    if (t.present) parts.push(`${LABEL[buyer]} token present on ${t.present} of ${t.total}`);
    if (t.empty) parts.push(`${LABEL[buyer]} entry present with empty signal on ${t.empty} of ${t.total}`);
    if (t.absent) parts.push(`${LABEL[buyer]} entry absent on ${t.absent} of ${t.total}`);
    if (parts.length) obs.push(parts.join(', '));
  }
  return obs;
}

/* Meta placements block: twin of proxy sdkBidding/section.js placementsBlock (parity-pinned). */

function emptyPlacements(state, copy) {
  return {
    state, copy, platform: null, importedAt: null, window: null, stale: false,
    counts: { known: 0, unknown: 0, notSeen: 0, dead: 0 },
    chainChecked: 0, chainPartial: 0, suppressed: [], findings: [], androidNote: null, formatNote: null,
  };
}

function platformOfGamUrl(url) {
  const m = /[?&]js=([^&#]*)/.exec(String(url || ''));
  const js = m ? m[1] : '';
  if (/^afma-sdk-i-/i.test(js)) return 'ios';
  if (/^afma-sdk-a-/i.test(js)) return 'android';
  return null;
}

function defaultPacks() {
  try { return getPacks(); } catch { return null; }
}

const reportOfPack = (pack) => (pack && pack.metaPlacementReport && typeof pack.metaPlacementReport === 'object' ? pack.metaPlacementReport : null);

function placementsBlock(list, mitm, usb, session, mappingOk, fetchRows, opts) {
  try {
    if (!mitm.length && usb.length) return emptyPlacements('usb', COPY.placementsUsb);
    if (!mitm.length) return emptyPlacements('no_mapping', COPY.placementsNoMapping);
    const o = opts && typeof opts === 'object' ? opts : {};
    const packs = o.packs !== undefined ? o.packs : defaultPacks();
    const captureMs = o.captureMs !== undefined ? o.captureMs : captureTimeMs(list);

    let mapping = null;
    if (mappingOk) {
      const g = mitm.find((x) => x.sel && x.sel.mapping);
      mapping = g ? g.sel.mapping : (selectMapping(session, null).mapping || null);
    }
    const platform = (mapping && mapping.platform) || mitm.map((g) => platformOfGamUrl(g.row.url)).find(Boolean) || null;
    const cfgPack = mitm.map((g) => (g.iu ? packForIu(packs, g.iu) : null)).find(Boolean) || null;
    const report = reportOfPack(cfgPack);
    const entry = report && platform && report[platform] && typeof report[platform] === 'object' ? report[platform] : null;

    const out = emptyPlacements('ok', null);
    out.platform = platform;
    out.importedAt = entry && entry.importedAt ? entry.importedAt : null;
    out.window = entry && entry.window ? { ...entry.window } : null;

    const cfg = mapping && entry ? evaluateConfigPlacements(mapping, entry, { captureMs }) : null;
    if (cfg && cfg.state === 'ok') {
      out.counts = { ...cfg.counts };
      out.suppressed = cfg.suppressed.slice();
      out.stale = !!cfg.stale;
      for (const f of cfg.findings) {
        out.findings.push({ code: f.code, level: f.level, unit: f.units.length ? f.units.join(', ') : null, placementId: f.placementId, bidId: null, count: 1, text: f.text });
      }
    }

    const grouped = new Map();
    let sawType = false;
    for (const g of mitm) {
      if (g.winner !== 'meta') continue;
      const ex = g.row.validData && g.row.validData.expanded;
      if (ex && ex.creativeSource && ex.creativeSource.source === 'no-fill') continue;
      const sel = g.sel && g.sel.mapping ? g.sel.mapping : null;
      const rowPlatform = (sel && sel.platform) || platform;
      const pack = packForIu(packs, g.iu);
      const ids = pack && pack.identity && pack.identity.metaAppIds && typeof pack.identity.metaAppIds === 'object' ? pack.identity.metaAppIds : null;
      const chain = placementChain(g.row, metaAdRequestFor(g.row, fetchRows), {
        mapping: sel, report: reportOfPack(pack) || {}, packMetaAppId: ids && rowPlatform && ids[rowPlatform] ? ids[rowPlatform] : null, platform: rowPlatform, unit: g.iu,
      });
      if (chain.status === 'na') continue;
      if (chain.status === 'partial') { out.chainPartial += 1; continue; }
      out.chainChecked += 1;
      if (chain.evidence && chain.evidence.definitionType) sawType = true;
      for (const is of chain.issues) {
        const pid = chain.links.gamResponse || chain.links.sdkRequest || null;
        const key = `${is.code}|${g.iu || ''}|${pid || ''}`;
        const hit = grouped.get(key);
        if (hit) { hit.count += 1; continue; }
        grouped.set(key, { code: is.code, level: is.level, unit: g.iu || null, placementId: pid, bidId: g.sdkBidId, count: 1, text: is.msg });
      }
    }
    for (const f of grouped.values()) out.findings.push(f);

    if (!mapping) { out.state = 'no_mapping'; out.copy = COPY.placementsNoMapping; }
    else if (!entry) { out.state = 'no_report'; out.copy = COPY.placementsNoReport; }
    else if (out.stale) { out.state = 'stale'; out.copy = COPY.placementsStale; }
    if (platform === 'android' && fetchRows.length === 0) out.androidNote = COPY.placementsAndroid;
    if (sawType) out.formatNote = COPY.placementsNotGraded;
    return out;
  } catch {
    return emptyPlacements('no_mapping', COPY.placementsNoMapping);
  }
}

/**
 * buildSdkBiddingSection(rows, { previous, mode, packs, captureMs }) -> section | null (mode 'off').
 * Same shape as the proxy's, with `source: 'browser-fallback'`. `packs` defaults to getPacks().
 */
export function buildSdkBiddingSection(rows, opts = {}) {
  const mode = opts && opts.mode ? opts.mode : 'shadow';
  if (mode === 'off') return null;
  const base = {
    version: 1,
    mode,
    source: 'browser-fallback',
    mapping: { source: null, reason: 'not_captured', copy: COPY.noMapping },
    buyers: { meta: { present: false }, liftoff: { present: false } },
    rows: [],
    demand: [],
    adtestExcluded: 0,
    demandHeader: COPY.demandHeader,
    previousBuild: { available: false, copy: COPY.noPrevious },
    placements: emptyPlacements('no_mapping', COPY.placementsNoMapping),
  };
  try {
    const list = (Array.isArray(rows) ? rows : []).filter((r) => r && typeof r === 'object');
    const gam = list.filter((r) => typeOf(r) === 'google' && /[?&]iu=/.test(String(r.url || ''))).map(gamInfo);
    const mitm = gam.filter((g) => !g.usb);
    const usb = gam.filter((g) => g.usb);

    const session = resolveSessionMapping(list);
    const mappingOk = session.source === 'pubsetting';
    const ctx = {
      fetchRows: metaFetchRows(list),
      fillRows: list.filter((r) => r && typeof r.url === 'string' && r.url.includes(FILL_RESULT_LABEL)),
      mappingCopy: '',
      metaAppIds: () => [],
    };

    if (mappingOk) {
      const buyerUnits = { meta: 0, liftoff: 0 };
      const appIds = new Set();
      const apps = session.appNames.map((n) => session.byApp[n]);
      for (const m of apps) {
        for (const id of Object.keys(m.units)) {
          for (const e of m.units[id]) {
            if (has(buyerUnits, e.buyer)) buyerUnits[e.buyer] += 1;
            if (e.buyer === 'meta' && e.appId) appIds.add(e.appId);
          }
        }
      }
      base.mapping = {
        source: 'pubsetting',
        appNames: session.appNames.slice(),
        platform: apps[0] ? apps[0].platform : null,
        unitCount: apps.reduce((n, m) => n + (m.unitCount || 0), 0),
        buyerUnits,
        selection: { exact: 0, single: 0, ambiguous: 0 },
        metaAppIds: [...appIds],
        metaAppIdCopy: COPY.metaAppId,
      };
      ctx.metaAppIds = (unit) => {
        const ids = new Set();
        for (const m of apps) for (const e of unitEntries(m, unit)) if (e.buyer === 'meta' && e.appId) ids.add(e.appId);
        return [...ids];
      };
    } else {
      const reason = mitm.length === 0 && usb.length > 0 ? 'usb' : session.reason;
      base.mapping = { source: null, reason, copy: mappingCopyFor(reason) };
      ctx.mappingCopy = base.mapping.copy;
      if (mitm.length) {
        base.mapping.observations = observationsFor(mitm);
        if (base.mapping.observations.length) {
          base.mapping.observationText = `${base.mapping.observations.join('; ')}; mapping not captured, not graded`;
        }
      }
    }

    if (mappingOk) {
      for (const g of mitm) {
        g.sel = selectMapping(session, g.msid);
        const key = g.sel.mapping ? g.sel.selection : 'ambiguous';
        base.mapping.selection[key] += 1;
        g.mappedBuyers = g.sel.mapping ? unitEntries(g.sel.mapping, g.iu).map((e) => e.buyer) : [];
      }
    }

    for (const buyer of BUYERS) {
      const present = gam.some((g) => (tokenOf(g, buyer) && tokenOf(g, buyer).entryFound) || g.winner === buyer
        || (g.mappedBuyers && g.mappedBuyers.includes(buyer)));
      base.buyers[buyer] = { present, ...(present ? {} : { copy: COPY.notInSession }) };
    }

    const out = [];
    if (mitm.length) {
      if (mappingOk) {
        const keys = new Map();
        for (const g of mitm) {
          if (!g.iu) continue;
          for (const buyer of BUYERS) {
            if ((g.mappedBuyers || []).includes(buyer) || g.winner === buyer) keys.set(`${buyer}|${g.iu}`, { buyer, unit: g.iu });
          }
        }
        const sorted = [...keys.values()].sort((a, b) => (a.buyer === b.buyer ? a.unit.localeCompare(b.unit) : BUYERS.indexOf(a.buyer) - BUYERS.indexOf(b.buyer)));
        for (const k of sorted) out.push(evalGroup(k.buyer, k.unit, mitm.filter((g) => g.iu === k.unit), ctx, 'full'));
      } else {
        for (const buyer of BUYERS) {
          const present = mitm.some((g) => (tokenOf(g, buyer) && tokenOf(g, buyer).entryFound) || g.winner === buyer);
          if (present) out.push(evalGroup(buyer, null, mitm, ctx, 'no_mapping'));
        }
      }
    }

    if (usb.length) {
      const units = [...new Set(usb.map((g) => g.iu).filter(Boolean))].sort();
      for (const unit of units) {
        const rs = usb.filter((g) => g.iu === unit);
        for (const buyer of BUYERS) {
          if (rs.some((g) => tokenOf(g, buyer) && tokenOf(g, buyer).entryFound)) out.push(evalGroup(buyer, unit, rs, ctx, 'usb'));
        }
      }
    }
    base.rows = out;

    const presentFor = (buyer, unit, rs) => rs.some((g) => (tokenOf(g, buyer) && tokenOf(g, buyer).entryFound) || g.winner === buyer
      || (g.mappedBuyers && g.mappedBuyers.includes(buyer)));
    base.demand = buildDemand(gam, presentFor, opts && opts.previous);
    base.adtestExcluded = gam.filter((g) => g.adtest).length;
    if (opts && opts.previous && typeof opts.previous === 'object') {
      base.previousBuild = { available: true, copy: 'Compared with the previous qaRunner build digest.' };
    }
    base.placements = placementsBlock(list, mitm, usb, session, mappingOk, ctx.fetchRows, opts);
    return base;
  } catch (e) {
    return { ...base, error: `sdk bidding section failed: ${e && e.message}` };
  }
}
